import React from 'react';
import { toast } from '../Toast';
import { ArrowLeft, Printer } from 'lucide-react';
import { SERVICE_TYPE_LABELS } from '../../services/crmApi';
import { getProgramByServiceType } from '../../config/programs';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Props { h: ReturnType<typeof useApplicationDetail>; }

const SERVICE_BADGE_STYLES: Record<string, string> = {
  pup_grant: 'bg-blue-400/30 text-blue-100',
  wsparcie_na_starcie: 'bg-emerald-400/30 text-emerald-100',
  kfs: 'bg-orange-400/30 text-orange-100',
  zus: 'bg-rose-400/30 text-rose-100',
  bhp: 'bg-red-400/30 text-red-100',
  szkolenia: 'bg-indigo-400/30 text-indigo-100',
  turnusy: 'bg-orange-400/30 text-orange-100',
  wycieczki: 'bg-teal-400/30 text-teal-100',
  pozyczki_rozwojowe: 'bg-cyan-400/30 text-cyan-100',
  dotacje_rozwojowe: 'bg-fuchsia-400/30 text-fuchsia-100',
};

export const DetailHeader: React.FC<Props> = ({ h }) => {
  const { application, navigate } = h;
  if (!application) return null;

  const sType = application.service_type || 'pup_grant';
  const program = getProgramByServiceType(sType);
  const gradient = program?.gradient || 'from-blue-600 to-indigo-600';
  const backRoute = program?.routeBase || '/crm';
  const productLabel = program?.label || 'Panel CRM';

  return (
    <div className={`bg-gradient-to-r ${gradient} text-white py-5 px-4 shadow-lg`}>
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate(backRoute)}
          className="flex items-center text-white/70 hover:text-white transition-colors mb-2"
        >
          <ArrowLeft size={18} className="mr-1.5" />
          Powrót do {productLabel}
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {application.first_name} {application.last_name}
          </h1>
          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${SERVICE_BADGE_STYLES[sType] || 'bg-white/20 text-white'}`}>
            {SERVICE_TYPE_LABELS[sType] || sType}
          </span>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-white/70 text-sm">
            ID: {application.submission_id}
            {application.loan_amount && sType !== 'pup_grant' && (
              <span className="ml-3">Pożyczka: {Number(application.loan_amount).toLocaleString('pl-PL')} zł</span>
            )}
          </p>
          <button
            onClick={async () => {
              const token = localStorage.getItem('grant_crm_token') || '';
              const res = await fetch(`${API_URL}/api/crm/applications/${application.id}/export-profile`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) { toast.error('Błąd eksportu profilu'); return; }
              const url = URL.createObjectURL(await res.blob());
              window.open(url, '_blank');
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }}
            className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Printer size={14} />
            Eksportuj profil
          </button>
        </div>
      </div>
    </div>
  );
};
